import React from 'react';
import type { ChipColor } from '@/types/chip';
import { Box, Typography, Paper, Grid, Chip, IconButton } from '@mui/material';
import { CheckCircle, Cancel, HelpOutline, CalendarMonth, Close, EventNote } from '@mui/icons-material';
import { PageDescription } from '@/components/help';
import { Show } from 'react-admin';
import { useNavigate } from 'react-router-dom';

const STATUS_COLORS: Record<string, ChipColor> = {
  Present: 'success',
  Absent: 'error',
  Excused: 'warning',
};

export const AttendanceDetail = () => {
  const navigate = useNavigate();

  return (
    <Show render={({ record }) => {
      if (!record) return null;

      const StatusIcon = record.status === 'Present' ? CheckCircle : record.status === 'Absent' ? Cancel : HelpOutline;

      return (
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <IconButton onClick={() => navigate('/attendance')} size="small" sx={{ color: 'text.secondary' }} aria-label="Close">
              <Close />
            </IconButton>
          </Box>

          <PageDescription
            entity="Attendance"
            description="Student attendance record for a specific tutoring session. Tracks presence, absence, and notes."
            icon={<EventNote />}
          />

          <Paper elevation={0} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #1565c0, #1976d2)', color: 'primary.contrastText', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <StatusIcon sx={{ fontSize: 48 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" fontWeight={700}>Attendance Record</Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{record.studentName || '—'} · {record.date || '—'}</Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Chip label={record.status} size="small" color={STATUS_COLORS[record.status]} />
                </Box>
              </Box>
            </Box>
          </Paper>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CheckCircle color="primary" />
                  <Typography variant="subtitle1" fontWeight={700}>Attendance Details</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Student</Typography>
                    <Typography variant="body1" fontWeight={500}>{record.studentName || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Session</Typography>
                    <Typography variant="body1">{record.sessionDescription || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Chip label={record.status} size="small" color={STATUS_COLORS[record.status]} />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarMonth fontSize="small" color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">Date</Typography>
                        <Typography variant="body1">{record.date || '—'}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700}>Notes</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="body1">{record.notes || 'No notes recorded'}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      );
    }} />
  );
};
